import { friendlyOrderTrackingOrderViewName } from "@redotech/order-tracking-common/default-order-tracking-order-views";
import { friendlyOrderTrackingShipmentViewName } from "@redotech/order-tracking-common/default-order-tracking-shipment-views";
import { friendlyViewName as purchaseOrderFriendlyViewName } from "@redotech/redo-fulfillment-common/views/default-purchase-order-views";
import { friendlyShipmentViewName } from "@redotech/redo-fulfillment-common/views/default-shipment-views";
import { friendlyViewName as workOrderFriendlyViewName } from "@redotech/redo-fulfillment-common/views/default-work-order-views";
import { friendlyViewName as outboundFriendlyViewName } from "@redotech/redo-fulfillment-operations-common/views/default-outbound-views";
import { friendlyViewName as catalogProductsFriendlyViewName } from "@redotech/redo-inventory-common/views/default-catalog-products-views";
import { friendlyViewName as cycleCountFriendlyViewName } from "@redotech/redo-inventory-common/views/default-cycle-count-views";
import { friendlyViewName as inventoryLevelsFriendlyViewName } from "@redotech/redo-inventory-common/views/default-inventory-levels-views";
import { friendlyViewName as replenishmentRequestFriendlyViewName } from "@redotech/redo-inventory-common/views/default-replenishment-request-views";
import { ClientFulfillmentGroupFolder } from "@redotech/redo-views-common/folders";
import {
  ViewTypesNames,
  viewTypes,
  viewWithIdTypes,
} from "@redotech/redo-views-common/views";
import {
  FlattenedItem,
  TreeFolderItem,
  TreeItem,
  TreeItemType,
  TreeViewItem,
} from "@redotech/redo-web/arbiter-components/navigation/folder-tree/types";
import { deepCopyObject } from "@redotech/util/object";
import { assertNever } from "@redotech/util/type";
import {
  getReturnOperationsNavFolderLabel,
  getReturnOperationsNavViewLabel,
} from "./return-nav-labels";

// Todo: also make folder generic
export interface FolderWithView<T extends keyof viewTypes> {
  folder: ClientFulfillmentGroupFolder;
  views: viewWithIdTypes[T][];
}

export function viewsAndFoldersToTree<T extends keyof viewTypes>({
  viewsWithoutFolders,
  foldersWithViews,
  urlPrefix,
  viewCounts,
  folderCounts,
  hideZeroNotifications,
}: {
  viewsWithoutFolders: viewWithIdTypes[T][];
  foldersWithViews: FolderWithView<T>[];
  urlPrefix: string;
  viewCounts?: Record<string, number>;
  folderCounts?: Record<string, any>;
  hideZeroNotifications: boolean;
}) {
  const allItemsWithPositions = [
    ...viewsWithoutFolders.map((view) => ({
      ...view,
      position: view.position,
      _id: view._id,
    })),
    ...foldersWithViews.map((folder) => ({
      ...folder,
      views: folder.views.sort((a, b) => a.position - b.position),
      position: folder.folder.position,
      _id: folder.folder._id,
    })),
  ];
  const sortedItems = allItemsWithPositions.sort((a, b) => {
    return a.position - b.position;
  });

  const treeItems: TreeItem[] = sortedItems.map((item) => {
    if ("folder" in item) {
      return folderToTreeItem({
        folder: item,
        urlPrefix,
        folderCounts,
        viewCounts,
        hideZeroNotifications,
      });
    } else {
      return viewToTreeItem({
        view: item,
        urlPrefix,
        viewCounts,
        hideZeroNotifications,
      });
    }
  });

  return treeItems;
}

function viewToTreeItem<T extends keyof viewTypes>({
  view,
  urlPrefix,
  viewCounts,
  hideZeroNotifications,
}: {
  view: viewWithIdTypes[T];
  urlPrefix: string;
  viewCounts?: Record<string, number>;
  hideZeroNotifications: boolean;
}): TreeViewItem {
  let friendlyName = view.name;
  switch (view.table) {
    case ViewTypesNames.RETURNS:
    case ViewTypesNames.CLAIMS:
    case ViewTypesNames.WARRANTIES:
    case ViewTypesNames.RECYCLE:
    case ViewTypesNames.WITHDRAWALS:
      friendlyName = getReturnOperationsNavViewLabel(view.name);
      break;
    case ViewTypesNames.FULFILLMENT_GROUPS:
      friendlyName = outboundFriendlyViewName(view);
      break;
    case ViewTypesNames.PURCHASE_ORDERS:
      friendlyName = purchaseOrderFriendlyViewName(view);
      break;
    case ViewTypesNames.SHIPMENTS:
      friendlyName = friendlyShipmentViewName(view.name);
      break;
    case ViewTypesNames.INVENTORY_LEVELS:
      friendlyName = inventoryLevelsFriendlyViewName(
        view as viewWithIdTypes["inventory-levels"],
      );
      break;
    case ViewTypesNames.CATALOG_PRODUCTS:
      friendlyName = catalogProductsFriendlyViewName(
        view as viewWithIdTypes["catalog-products"],
      );
      break;
    case ViewTypesNames.ORDER_TRACKING_SHIPMENTS:
      friendlyName = friendlyOrderTrackingShipmentViewName(view.name);
      break;
    case ViewTypesNames.ORDER_TRACKING_ORDERS:
      friendlyName = friendlyOrderTrackingOrderViewName(view.name);
      break;
    case ViewTypesNames.WORK_ORDERS:
      friendlyName = workOrderFriendlyViewName(
        view as viewWithIdTypes["work-orders"],
      );
      break;
    case ViewTypesNames.REPLENISHMENT_REQUESTS:
      friendlyName = replenishmentRequestFriendlyViewName(
        view as viewWithIdTypes["replenishment-requests"],
      );
      break;
    case ViewTypesNames.CYCLE_COUNTS:
      friendlyName = cycleCountFriendlyViewName(
        view as viewWithIdTypes["cycle-counts"],
      );
      break;
  }

  return {
    id: view._id,
    name: view.name,
    actionHref: `${urlPrefix}/${encodeURIComponent(view.name)}`,
    friendlyName,
    type: TreeItemType.View,
    notificationCount:
      viewCounts?.[view?._id] === 0 && hideZeroNotifications
        ? undefined
        : viewCounts?.[view?._id],
  };
}

function folderToTreeItem<T extends keyof viewTypes>({
  folder,
  urlPrefix,
  folderCounts,
  viewCounts,
  hideZeroNotifications,
}: {
  folder: FolderWithView<T>;
  urlPrefix: string;
  folderCounts?: Record<string, number>;
  viewCounts?: Record<string, number>;
  hideZeroNotifications: boolean;
}): TreeFolderItem {
  return {
    id: folder.folder._id,
    name: folder.folder.name,
    friendlyName: getReturnOperationsNavFolderLabel(folder.folder.name),
    type: TreeItemType.Folder,
    children: folder.views.map((view) =>
      viewToTreeItem({ view, urlPrefix, viewCounts, hideZeroNotifications }),
    ),
    notificationCount:
      folderCounts?.[folder?.folder?._id] === 0 && hideZeroNotifications
        ? undefined
        : folderCounts?.[folder?.folder?._id],
  };
}

export function treeToViewsAndFolders<T extends keyof viewTypes>(
  flattenedItems: FlattenedItem[],
  allViews: viewWithIdTypes[T][],
  allFolders: ClientFulfillmentGroupFolder[],
) {
  const viewsCopy = allViews.map((view) => deepCopyObject(view));
  const foldersCopy = allFolders.map((folder) => deepCopyObject(folder));

  const newFolders: ClientFulfillmentGroupFolder[] = [];
  const newViews: viewWithIdTypes[T][] = [];

  for (const item of flattenedItems) {
    switch (item.type) {
      case TreeItemType.View: {
        const matchingView = viewsCopy.find((view) => view._id === item.id);
        if (matchingView) {
          if (item.parentId) {
            matchingView.folderId = item.parentId.toString();
          } else {
            matchingView.folderId = null;
          }
          matchingView.position = item.index;
          newViews.push(matchingView);
        }
        break;
      }
      case TreeItemType.Folder: {
        const matchingFolder = foldersCopy.find(
          (folder) => folder._id === item.id,
        );

        if (matchingFolder) {
          matchingFolder.position = item.index;
          newFolders.push(matchingFolder);
        }
        break;
      }
      default:
        assertNever(item);
    }
  }

  return { views: newViews, folders: newFolders };
}
